//? React Query
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Cart API
import { questionCartAPI } from "./questionCartAPI";

//* Types
import { type AddToCartPayload } from "./types/types";

const QUESTION_CART_KEY = ["question-cart"] as const;

/**
 * Data hook for the persistent question cart. Mirrors the project pattern:
 * React Query wrapping an API object, token passed explicitly.
 */
export const useQuestionCart = (token: string) => {
  const queryClient = useQueryClient();

  const {
    data: items = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: QUESTION_CART_KEY,
    queryFn: () => questionCartAPI.getAll(token),
    enabled: !!token,
    staleTime: 60 * 1000,
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUESTION_CART_KEY });
  };

  const addMutation = useMutation({
    mutationFn: (payload: AddToCartPayload) => questionCartAPI.add(payload, token),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (itemId: string) => questionCartAPI.remove(itemId, token),
    onSuccess: invalidate,
  });

  // Remoção em lote: deleta todos em paralelo e invalida o cache uma única vez,
  // evitando refetches concorrentes que atropelavam o loop sequencial.
  const removeManyMutation = useMutation({
    mutationFn: async (itemIds: string[]) => {
      await Promise.all(itemIds.map((id) => questionCartAPI.remove(id, token)));
    },
    onSuccess: invalidate,
  });

  const clearMutation = useMutation({
    mutationFn: () => questionCartAPI.clear(token),
    onSuccess: invalidate,
  });

  /**
   * Derives whether a question is already in the cart, without an extra request.
   */
  const isInCart = (questionId: string): boolean =>
    items.some((item) => item.questionId === questionId);

  return {
    items,
    count: items.length,
    loading: isLoading || isFetching,
    isError,
    error: isError ? error : null,
    refetch,
    isInCart,
    addToCart: addMutation.mutateAsync,
    removeFromCart: removeMutation.mutateAsync,
    removeManyFromCart: removeManyMutation.mutateAsync,
    clearCart: clearMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending || removeManyMutation.isPending,
    isClearing: clearMutation.isPending,
  };
};
